import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  KycContextType,
  OrganisationFormValues,
  BankFormValues,
  ApiResponse,
} from "../types/kyc.types";
import { useAPI } from "../../../../hooks/useAPI";
import { ENDPOINTS } from "../../../../constants/endpoints.constant";
import { postCall } from "../../../../constants/apicalls.constant";
import { message } from "antd";

const KycContext = createContext<KycContextType | undefined>(undefined);

const STORAGE_KEY = "kyc_flow_data";

export const KycProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [organisationDetails, setOrganisationDetailsState] = useState<
    Partial<OrganisationFormValues>
  >({});
  const [bankDetails, setBankDetailsState] = useState<Partial<BankFormValues>>(
    {},
  );
  const [verificationStatus, setVerificationStatus] = useState({
    pan: false,
    aadhar: false,
    gst: false,
  });

  const { loading, trigger } = useAPI<ApiResponse>(ENDPOINTS.me);

  // Load from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.organisationDetails)
          setOrganisationDetailsState(parsed.organisationDetails);
        if (parsed.bankDetails) setBankDetailsState(parsed.bankDetails);
        if (parsed.currentStep) setCurrentStep(parsed.currentStep);
        if (parsed.verificationStatus)
          setVerificationStatus(parsed.verificationStatus);
      } catch (e) {
        console.error("Failed to load KYC data from storage", e);
      }
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        organisationDetails,
        bankDetails,
        currentStep,
        verificationStatus,
      }),
    );
  }, [organisationDetails, bankDetails, currentStep, verificationStatus]);

  const setOrganisationDetails = useCallback(
    (details: Partial<OrganisationFormValues>) => {
      setOrganisationDetailsState((prev) => ({ ...prev, ...details }));
    },
    [],
  );

  const setBankDetails = useCallback((details: Partial<BankFormValues>) => {
    setBankDetailsState((prev) => ({ ...prev, ...details }));
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const submitKyc = useCallback(async () => {
    const payload = { organisationDetails, bankDetails };
    try {
      const res = await trigger(postCall(payload));
      if (res?.status) {
        message.success("KYC submitted successfully!");
        localStorage.removeItem(STORAGE_KEY);
      } else {
        message.error(res?.message || "Submission failed");
      }
    } catch (error) {
      message.error("An error occurred during submission");
    }
  }, [organisationDetails, bankDetails, trigger]);

  return (
    <KycContext.Provider
      value={{
        currentStep,
        organisationDetails,
        bankDetails,
        verificationStatus,
        setOrganisationDetails,
        setBankDetails,
        setVerificationStatus,
        nextStep,
        prevStep,
        submitKyc,
        loading,
      }}
    >
      {children}
    </KycContext.Provider>
  );
};

export const useKyc = () => {
  const context = useContext(KycContext);
  if (!context) {
    throw new Error("useKyc must be used within a KycProvider");
  }
  return context;
};
